import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { getBudgetCats } from '@/utils';
import { generateRecurringDates } from '../modal-add-tx/recurrence';
import ModalForm from '@/common/components/modal-form';
import InputField from '@/common/components/forms/input-field';
import { evaluateCalc } from '@/utils/calc';
import CategoriesField from '@/common/components/forms/categories-field';
import ToggleField from '@/common/components/forms/toggle-field';
import WeekdayPicker from '@/common/components/forms/weekday-picker';
import TutorialSpotlight from '@/common/components/tutorial-spotlight';
import formsStyle from '@/common/components/forms/forms.module.scss';
const EDIT_SERIES_STEPS = [
    {
        title: 'Вкладки серии',
        text: '«Изменить» — изменить сумму, описание и категорию всех будущих операций серии. «Продлить» — добавить новые операции к серии.',
        selector: '[data-tut="series-tabs"]',
        place: 'bottom',
    },
    {
        title: 'Удаление серии',
        text: 'Внизу вкладки «Изменить» можно удалить все операции серии начиная с выбранной даты.',
        selector: '[data-tut="series-delete"]',
        place: 'top',
    },
];
function detectFrequency(txs) {
    if (txs.length < 2)
        return { frequency: 'monthly', weekdays: Array(7).fill(false), monthDay: 1 };
    const dates = txs.map(t => new Date(t.date + 'T00:00:00')).sort((a, b) => a.getTime() - b.getTime());
    // Check if monthly: all on roughly same day
    const days = dates.map(d => d.getDate());
    const allSameDay = days.every(d => Math.abs(d - days[0]) <= 3);
    if (allSameDay && dates.length >= 2) {
        const gap = (dates[1].getFullYear() - dates[0].getFullYear()) * 12 + dates[1].getMonth() - dates[0].getMonth();
        if (gap >= 1) {
            return { frequency: 'monthly', weekdays: Array(7).fill(false), monthDay: days[0] };
        }
    }
    // Assume weekly
    const weekdays = Array(7).fill(false);
    dates.forEach(d => {
        const wd = (d.getDay() + 6) % 7; // Monday = 0
        weekdays[wd] = true;
    });
    return { frequency: 'weekly', weekdays, monthDay: days[0] };
}
export default function EditSeriesModal({ tx, budget, allSeriesTxs, onClose, onUpdateFrom, onDeleteFrom, onExtend, }) {
    const fromDate = tx.date;
    const futureTxs = allSeriesTxs.filter(t => t.date >= fromDate).sort((a, b) => a.date.localeCompare(b.date));
    const detected = useMemo(() => detectFrequency(allSeriesTxs), [allSeriesTxs]);
    // Edit fields
    const [description, setDescription] = useState(tx.description);
    const [amount, setAmount] = useState(String(tx.planned ?? tx.actual ?? ''));
    const [category, setCategory] = useState(tx.category || '');
    const isPlanned = tx.planned != null;
    const [confirmDelete, setConfirmDelete] = useState(false);
    // Extend
    const [extendCount, setExtendCount] = useState(detected.frequency === 'weekly' ? 4 : 3);
    const [extendFrequency, setExtendFrequency] = useState(detected.frequency);
    const [extendWeekdays, setExtendWeekdays] = useState(detected.weekdays);
    const [extendMonthDay, setExtendMonthDay] = useState(detected.monthDay);
    const [tab, setTab] = useState('edit');
    const availCats = getBudgetCats(budget).filter((c) => c.type === tx.type);
    const handleSave = () => {
        const num = evaluateCalc(amount) ?? parseFloat(amount);
        if (Number.isNaN(num) || num <= 0)
            return;
        const updates = {
            description: description.trim() || tx.description,
            category: category || null,
        };
        if (isPlanned) {
            updates.planned = num;
        }
        else {
            updates.actual = num;
        }
        onUpdateFrom(tx.seriesId, fromDate, updates);
        onClose();
    };
    const handleDelete = () => {
        onDeleteFrom(tx.seriesId, fromDate);
        onClose();
    };
    const handleExtend = () => {
        if (extendCount <= 0)
            return;
        // Find last date in series
        const lastTx = allSeriesTxs.sort((a, b) => b.date.localeCompare(a.date))[0];
        const lastDate = new Date(lastTx.date + 'T00:00:00');
        // Start generating from the day after the last tx
        let startDate;
        if (extendFrequency === 'weekly') {
            const next = new Date(lastDate);
            next.setDate(next.getDate() + 1);
            startDate = formatDate(next);
        }
        else {
            const next = new Date(lastDate);
            next.setMonth(next.getMonth() + 1);
            startDate = formatDate(next);
        }
        const dates = generateRecurringDates(startDate, {
            enabled: true,
            frequency: extendFrequency,
            count: extendCount,
            weekdays: extendWeekdays,
            monthDay: extendMonthDay,
        });
        const newTxs = dates.map(date => ({
            date,
            description: tx.description,
            planned: tx.planned,
            actual: tx.actual,
            type: tx.type,
            category: tx.category,
            seriesId: tx.seriesId,
            ...(tx.fromSavings ? { fromSavings: true } : {}),
        }));
        onExtend(tx.seriesId, newTxs);
        onClose();
    };
    return (_jsxs(ModalForm, { onSubmit: tab === 'edit' ? handleSave : handleExtend, onClose: onClose, headlineText: "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435 \u0441\u0435\u0440\u0438\u0438", submitButtonText: tab === 'edit' ? 'Сохранить' : 'Продлить', tabs: [
            { label: 'Изменить', id: 'edit' },
            { label: 'Продлить', id: 'extend' },
        ], tabsDataTut: "series-tabs", selectedTab: tab, onTabSelect: setTab, children: [tab === 'edit' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }, children: ["\u0418\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F \u043F\u0440\u0438\u043C\u0435\u043D\u044F\u0442\u0441\u044F \u043A ", futureTxs.length, " \u043E\u043F\u0435\u0440\u0430\u0446\u0438", futureTxs.length === 1 ? 'и' : futureTxs.length < 5 ? 'ям' : 'ям', " \u043E\u0442 ", fromDate, " \u0438 \u0434\u0430\u043B\u0435\u0435."] }), _jsx(CategoriesField, { label: "\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044F", categories: availCats, selectedCategory: category, onCategoryChange: setCategory, className: formsStyle['form-group'] }), _jsxs("div", { className: formsStyle['form-row'], children: [_jsx(InputField, { label: "\u0421\u0443\u043C\u043C\u0430", value: amount, placeholder: "0", onSetValue: setAmount, inputType: "calc" }), _jsx(InputField, { label: "\u0414\u0430\u0442\u0430 \u043D\u0430\u0447\u0430\u043B\u0430", value: fromDate, onSetValue: () => { }, inputType: "date", disabled: true })] }), _jsx(InputField, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", value: description, placeholder: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", onSetValue: setDescription }), _jsx("div", { "data-tut": "series-delete", style: { borderTop: '1px solid var(--border-light)', marginTop: 16, paddingTop: 16 }, children: !confirmDelete ? (_jsxs("button", { type: "button", onClick: () => setConfirmDelete(true), style: {
                                background: 'none',
                                border: 'none',
                                color: 'var(--expense)',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500,
                                padding: 0,
                            }, children: ["\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0432\u0441\u0435 \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0438 \u0441\u0435\u0440\u0438\u0438 \u043E\u0442 ", fromDate] })) : (_jsxs("div", { style: {
                                padding: '8px 10px',
                                background: 'var(--expense-bg)',
                                borderRadius: 'var(--r-sm)',
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                            }, children: [_jsxs("span", { style: { flex: 1, fontSize: 13, color: 'var(--expense)', fontWeight: 500 }, children: ["\u0423\u0434\u0430\u043B\u0438\u0442\u044C ", futureTxs.length, " \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0439?"] }), _jsx("button", { type: "button", onClick: handleDelete, style: {
                                        background: 'var(--expense)',
                                        color: '#fff',
                                        padding: '4px 10px',
                                        borderRadius: 'var(--r-sm)',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        border: 'none',
                                        cursor: 'pointer',
                                    }, children: "\u0414\u0430" }), _jsx("button", { type: "button", onClick: () => setConfirmDelete(false), style: {
                                        background: 'var(--card)',
                                        color: 'var(--text-2)',
                                        padding: '4px 10px',
                                        borderRadius: 'var(--r-sm)',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        border: '1px solid var(--border)',
                                        cursor: 'pointer',
                                    }, children: "\u041D\u0435\u0442" })] })) })] })), tab === 'extend' && (_jsxs(_Fragment, { children: [_jsx("div", { style: { fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043D\u043E\u0432\u044B\u0435 \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0438 \u043A \u0441\u0435\u0440\u0438\u0438 \u043F\u043E\u0441\u043B\u0435 \u043F\u043E\u0441\u043B\u0435\u0434\u043D\u0435\u0439 \u0441\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u044E\u0449\u0435\u0439." }), _jsx(ToggleField, { isOn: extendFrequency === 'weekly', label: "\u0427\u0430\u0441\u0442\u043E\u0442\u0430", onLabel: "\u0415\u0436\u0435\u043D\u0435\u0434\u0435\u043B\u044C\u043D\u0430\u044F", offLabel: "\u0415\u0436\u0435\u043C\u0435\u0441\u044F\u0447\u043D\u0430\u044F", onToggleValue: (isWeekly) => {
                            setExtendFrequency(isWeekly ? 'weekly' : 'monthly');
                            setExtendCount(isWeekly ? 4 : 3);
                        }, className: formsStyle['form-group'] }), _jsx(InputField, { label: extendFrequency === 'weekly' ? 'Кол-во недель' : 'Кол-во месяцев', value: extendCount === 0 ? '' : String(extendCount), onSetValue: (v) => {
                            const n = parseInt(v);
                            setExtendCount(v === '' ? 0 : (Number.isNaN(n) ? extendCount : Math.max(1, n)));
                        }, inputType: "amount" }), extendFrequency === 'weekly' ? (_jsx(WeekdayPicker, { label: "\u0414\u043D\u0438 \u043D\u0435\u0434\u0435\u043B\u0438", selected: extendWeekdays, onChange: setExtendWeekdays, weekStart: budget.weekStart })) : (_jsx(InputField, { label: "\u0414\u0435\u043D\u044C \u043C\u0435\u0441\u044F\u0446\u0430", value: extendMonthDay === 0 ? '' : String(extendMonthDay), onSetValue: (v) => {
                            const n = parseInt(v);
                            setExtendMonthDay(v === '' ? 0 : (Number.isNaN(n) ? extendMonthDay : Math.min(31, Math.max(1, n))));
                        }, inputType: "amount" }))] })), _jsx(TutorialSpotlight, { steps: EDIT_SERIES_STEPS, storageKey: "tut_edit_series_v1" })] }));
}
function formatDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
